import { AiFeaturePreset } from '../../../../models/AiFeaturePreset'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetFeaturesQuery, useUpdateInputParametersMutation } from '../../../../store/ai/ai'
import { useCallback, useEffect, useState } from 'react'
import { AiFeature, TemplateInputParameters } from '../../../../models/AiFeature'
import { useCreateNodeMutation, useGetEffectiveNodeQuery, useUpdateNodeMutation } from '../../../../store/ai/inputParametersHierarchy'
import { InputParameterHierarchyType } from '../../../../components/InputParameterHierarchy/model'
import { InputParametersHierarchyLevel } from '../../../../models/InputParametersHierarchy'

const useGlobalPresetInputParameters = (preset?: AiFeaturePreset | null) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: aiFeatures, isLoading: featuresLoading } = useGetFeaturesQuery(
    {
      accountId,
      pageRequest: { page: 0, size: 500 },
      includeRestricted: true,
    },
    { refetchOnMountOrArgChange: true },
  )

  const [updateInputParameter] = useUpdateInputParametersMutation()

  const [presetAiFeatures, setPresetAiFeatures] = useState<AiFeature[] | null>(null)
  const [updateLoading, setUpdateLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!preset || !aiFeatures?.data) {
      return
    }
    setPresetAiFeatures(aiFeatures.data.filter((feature) => feature.preset?.id === preset.id))
  }, [aiFeatures, preset])

  const updateInputParameters = useCallback(
    async (inputParameters: { [key: string]: any }) => {
      if (!presetAiFeatures) {
        return
      }
      setUpdateLoading(true)
      for (const updatableFeature of presetAiFeatures) {
        try {
          await updateInputParameter({
            aiFeatureId: updatableFeature.id,
            accountId,
            payload: {
              inputParameters,
            },
          })
        } catch (e) {
          console.error(e)
        }
      }
      setUpdateLoading(false)
    },
    [accountId, updateInputParameter, presetAiFeatures],
  )

  return { isLoading: featuresLoading, presetAiFeatures, updateInputParameters, updateLoading }
}

export const useGlobalFormPresetInputParameters = (preset?: AiFeaturePreset | null) => {
  const { isLoading, presetAiFeatures, updateInputParameters, updateLoading } = useGlobalPresetInputParameters(preset)

  const [inputParameters, setInputParameters] = useState<{ [key: string]: any } | null>(null)

  useEffect(() => {
    if (!preset || isLoading || !presetAiFeatures) {
      return
    }
    const aiFeature = presetAiFeatures.find((value) => !!value.inputParameters)
    setInputParameters(aiFeature?.inputParameters ?? {})
  }, [presetAiFeatures, preset, isLoading])

  return {
    presetInputParameters: inputParameters,
    updateInputParameters,
    updateLoading,
  }
}

export const useGlobalTemplatePresetInputParameters = (preset?: AiFeaturePreset | null) => {
  const { isLoading, presetAiFeatures, updateInputParameters, updateLoading } = useGlobalPresetInputParameters(preset)

  const [inputParameters, setInputParameters] = useState<TemplateInputParameters | null>(null)
  const [defaultInputParameters, setDefaultInputParameters] = useState<TemplateInputParameters | null>(null)

  useEffect(() => {
    if (!preset || isLoading || !presetAiFeatures) {
      return
    }
    const defaultTemplate = preset.inputParametersSchema?.properties?.template.default
    const aiFeature = presetAiFeatures.find((value) => !!value.inputParameters)
    const example_1 = aiFeature?.inputParameters?.example_1 || ''
    const example_2 = aiFeature?.inputParameters?.example_2 || ''
    const template = aiFeature?.inputParameters?.template || defaultTemplate
    setInputParameters({ example_1, example_2, template })
    setDefaultInputParameters({ example_1: '', example_2: '', template: defaultTemplate })
  }, [presetAiFeatures, preset, isLoading])

  return {
    presetInputParameters: inputParameters,
    presetDefaultInputParameters: defaultInputParameters,
    updateInputParameters,
    updateLoading,
  }
}

export const useUnifiedGlobalFormPresetInputParameters = (preset?: AiFeaturePreset | null) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: aiFeatures, isLoading: featuresLoading } = useGetFeaturesQuery(
    {
      accountId,
      pageRequest: { page: 0, size: 500 },
      includeRestricted: true,
    },
    { refetchOnMountOrArgChange: true },
  )

  const { data: effectiveNode, isFetching: nodeLoading } = useGetEffectiveNodeQuery(
    {
      accountId,
      type: InputParameterHierarchyType.AI_FEATURE_PRESET,
      typeId: preset?.id ?? '',
      context: { effective: true, level: InputParametersHierarchyLevel.GLOBAL },
    },
    { refetchOnMountOrArgChange: true, skip: !preset?.id },
  )

  const [triggerCreateNode] = useCreateNodeMutation()
  const [triggerUpdateNode] = useUpdateNodeMutation()

  const [presetAiFeatures, setPresetAiFeatures] = useState<AiFeature[] | null>(null)
  const [inputParameters, setInputParameters] = useState<Record<string, unknown> | null>(null)
  const [updateLoading, setUpdateLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!preset || !aiFeatures?.data) {
      return
    }
    setPresetAiFeatures(aiFeatures.data.filter((feature) => feature.preset?.id === preset.id))
  }, [aiFeatures, preset])

  useEffect(() => {
    if (featuresLoading || nodeLoading) {
      return
    }
    if (effectiveNode) {
      setInputParameters(effectiveNode.inputParameters)
    } else if (presetAiFeatures) {
      const aiFeature = presetAiFeatures.find((value) => !!value.inputParameters)
      setInputParameters(aiFeature?.inputParameters ?? {})
    } else {
      setInputParameters({})
    }
  }, [presetAiFeatures, featuresLoading, effectiveNode, nodeLoading])

  const updateInputParameters = useCallback(
    async (newInputParameters: Record<string, unknown>) => {
      if (!preset?.id) {
        return
      }
      setUpdateLoading(true)
      try {
        if (effectiveNode) {
          await triggerUpdateNode({
            accountId,
            type: InputParameterHierarchyType.AI_FEATURE_PRESET,
            typeId: preset.id,
            nodeId: effectiveNode.id,
            node: { inputParameters: newInputParameters },
          })
        } else {
          await triggerCreateNode({
            accountId,
            type: InputParameterHierarchyType.AI_FEATURE_PRESET,
            typeId: preset.id,
            node: {
              hierarchyId: { level: InputParametersHierarchyLevel.GLOBAL },
              inputParameters: newInputParameters,
            },
          })
        }
        setInputParameters(newInputParameters)
      } catch (e) {
        console.error('Failed to update input parameters:', e)
      } finally {
        setUpdateLoading(false)
      }
    },
    [accountId, preset?.id, effectiveNode, triggerCreateNode, triggerUpdateNode],
  )

  return {
    presetInputParameters: inputParameters,
    updateInputParameters,
    updateLoading,
  }
}
